/**
 * @fileoverview Footer.jsx
 * 
 * © 2026 KITH Travels (Pvt) Ltd. All rights reserved.
 * This software is the confidential and proprietary information of KITH Travels.
 * You shall not disclose such Confidential Information and shall use it only in 
 * accordance with the terms of the license agreement you entered into with KITH Travels.
 */
import React from 'react'
import CopyRights from './CopyRights'
import { CompanyLinks, Contact, IMG, NavItems, SocialMedia, SupportLinks } from '../../assets/Assets'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

function Footer() {
  const { t } = useTranslation();

  return (
    <div className='w-full bg-gray-50 pt-20 flex flex-col'>
        <div className='px-[5%] md:px-[10%] pb-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8'>
            
            {/* Brand Section */}
            <div className='flex flex-col gap-5 text-left'>
                <img src={IMG.KITH_logo} alt="KITH Travels Logo" width="185" height="80" className='w-28' loading="lazy" />
                <p className='text-gray-500 text-xs leading-relaxed max-w-xs'>
                    {t('footer.brand_desc')}
                </p>
                <div className='flex gap-3 mt-2'>
                    {SocialMedia.map((item, index) => (
                        <a key={index} href={item.link} aria-label={`Follow KITH Travels on ${item.name}`} className='w-9 h-9 rounded-full bg-white flex items-center justify-center text-gray-500 hover:bg-KITH-primary hover:text-white transition-all shadow-sm border border-gray-100' target="_blank" rel="noopener noreferrer">
                            <i className={`${item.icon} text-sm`}></i>
                        </a>
                    ))}
                </div>
            </div>

            {/* Quick Links */}
            <div className='flex flex-col gap-5 text-left'>
                <h3 className='text-[11px] font-bold text-gray-800 uppercase tracking-widest'>{t('footer.explore')}</h3>
                <ul className='flex flex-col gap-2.5'>
                    {NavItems.map((link, index) => (
                        <li key={index}>
                            <Link to={link.path} className='text-gray-500 hover:text-KITH-primary transition-colors text-sm font-medium'>
                                {t('nav.' + link.name.toLowerCase().replace(' ', '_'))}
                            </Link>
                        </li>
                    ))}
                    <li>
                        <Link to="/hotels" className='text-gray-500 hover:text-KITH-primary transition-colors text-sm font-medium'>{t('footer.links.hotels')}</Link>
                    </li>
                    <li>
                        <Link to="/blog" className='text-gray-500 hover:text-KITH-primary transition-colors text-sm font-medium'>{t('footer.links.blog')}</Link>
                    </li>
                    <li>
                        <Link to="/when-to-visit" className='text-gray-500 hover:text-KITH-primary transition-colors text-sm font-medium'>{t('footer.links.when_to_travel')}</Link>
                    </li>
                </ul>
            </div>

            {/* Company */}
            <div className='flex flex-col gap-5 text-left'>
                <h3 className='text-[11px] font-bold text-gray-800 uppercase tracking-widest'>{t('footer.company')}</h3>
                <ul className='flex flex-col gap-2.5'>
                    {CompanyLinks.map((link, index) => (
                        <li key={index}>
                            <Link to={link.path} className='text-gray-500 hover:text-KITH-primary transition-colors text-sm font-medium'>{t('nav.' + link.name.toLowerCase().replace(/ & /g, '_and_').replace(/ /g, '_'))}</Link>
                        </li>
                    ))}
                </ul>
            </div>

            {/* Support */}
            <div className='flex flex-col gap-5 text-left'>
                <h3 className='text-[11px] font-bold text-gray-800 uppercase tracking-widest'>{t('footer.support')}</h3>
                <ul className='flex flex-col gap-2.5'>
                    {SupportLinks.map((link, index) => (
                        <li key={index}>
                            <Link to={link.path} className='text-gray-500 hover:text-KITH-primary transition-colors text-sm font-medium'>{t('nav.' + link.name.toLowerCase().replace(/ & /g, '_and_').replace(/ /g, '_'))}</Link>
                        </li>
                    ))}
                </ul>
            </div>
        </div>

        {/* Contact Info Bar */}
        <div className='px-[5%] md:px-[10%] py-6 border-t border-gray-200 flex flex-wrap justify-between items-center gap-4'>
            <div className='flex items-center gap-6'>
                <div className='flex items-center gap-2'>
                    <i className='fi fi-br-marker text-KITH-primary text-xs'></i>
                    <span className='text-[11px] text-gray-500 font-medium uppercase tracking-wide'>{Contact.Address}</span>
                </div>
            </div>
            <div className='flex items-center gap-6'>
                <div className='flex items-center gap-2'>
                    <i className='fi fi-br-envelope text-KITH-primary text-xs'></i>
                    <span className='text-[11px] text-gray-500 font-medium tracking-wide'>{Contact.Email}</span>
                </div>
                <div className='flex items-center gap-2 font-bold text-gray-800'>
                    <i className='fi fi-br-phone-call text-KITH-primary text-xs'></i>
                    <span className='text-[11px] tracking-widest'>{Contact.Hotline}</span>
                </div>
            </div>
        </div>

        <CopyRights />
    </div>
  )
}

export default Footer